import { Box, Group, type RenderTreeNodePayload, Text, Tree, type TreeNodeData, useTree } from "@mantine/core";
import { useMemo } from "react";
import { IconChevronRight } from "../icons";

// The names a run left behind, as they were when it ended: a program that is still going has nothing to show yet.
// /python and /javascript both fill this, which is why the heading the second list waits under is a prop — one page
// gathers what an import brought in, the other the routines the script wrote down.
export function Variables({ busy, scope, empty, section }: VariablesProps) {
  const tree = useTree();
  // Identity is what Tree reads a change from, so the same snapshot must not become a new set of nodes on a render.
  const data = useMemo(() => toData(scope, section), [scope, section]);

  return (
    <>
      <Box px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <Text size="sm" fw={500}>Variables</Text>
      </Box>
      <Box p="xs" style={{ flex: 1, overflow: "auto" }} aria-disabled={busy || undefined}>
        {data.length === 0
          ? <Text size="sm" c="dimmed" px="xs">{empty}</Text>
          : <Tree data={data} tree={tree} aria-label="Variables" levelOffset="md" renderNode={renderVariable} />}
      </Box>
    </>
  );
}

interface VariablesProps {
  busy: boolean;
  scope: Scope | null;
  empty: string;
  // What the second list is called, and what one row of it counts as: "1 name" reads differently from "1 routine".
  section: { label: string; one: string; many: string };
}

// One name a run left bound, read by whichever interpreter was behind the page: `children` is what a value opens to.
export interface Variable {
  name: string;
  kind: string;
  value: string;
  children?: Variable[];
  note?: boolean;
  // Only the heading the second list is gathered under, which is a row of this panel's making rather than a script's.
  heading?: boolean;
}

// The two halves a scope is read into: what the program is working with, and what waits under the heading.
export interface Scope {
  variables: Variable[];
  section: Variable[];
}

function renderVariable({ node, expanded, hasChildren, elementProps }: RenderTreeNodePayload) {
  const { name, kind, value, note, heading } = (node as VariableNode).variable;
  const { style, ...rest } = elementProps;

  return (
    <Group {...rest} style={{ ...style, cursor: hasChildren ? "pointer" : "default" }} gap="xs" wrap="nowrap">
      <IconChevronRight
        size="0.8rem"
        // A leaf keeps the space the chevron would take, so every row at a level starts at the same column.
        style={{
          flex: "0 0 auto",
          visibility: hasChildren ? undefined : "hidden",
          transform: expanded ? "rotate(90deg)" : undefined,
        }}
      />
      {note ? <Text size="xs" c="dimmed">{value}</Text> : (
        <>
          <Text size="xs" fw={heading ? 500 : undefined} ff={heading ? undefined : "monospace"}>{name}</Text>
          <Text size="xs" c="dimmed">{kind}</Text>
          {/* The value is what somebody came to read, so it is the one thing on the row in the page's brightest ink. */}
          <Text size="xs" ff="monospace" c="bright" flex={1} miw={0} truncate="end" title={value}>{value}</Text>
        </>
      )}
    </Group>
  );
}

// The second list goes under one heading at the bottom, closed: what is in it is not what the run is about.
function toData(scope: Scope | null, section: VariablesProps["section"]): VariableNode[] {
  if (!scope) return [];
  const defined = scope.variables.map((variable, index) => toNode(variable, String(index)));
  if (scope.section.length === 0) return defined;

  const count = scope.section.length;
  return [...defined, {
    value: "section",
    label: section.label,
    variable: {
      name: section.label,
      kind: `${count} ${count === 1 ? section.one : section.many}`,
      value: "",
      heading: true,
    },
    children: scope.section.map((variable, index) => toNode(variable, `section.${index}`)),
  }];
}

function toNode(variable: Variable, path: string): VariableNode {
  return {
    value: path,
    label: variable.name,
    variable,
    children: variable.children?.map((child, index) => toNode(child, `${path}.${index}`)),
  };
}

// The variable a row was built from, carried on the node so `renderNode` has more than a label to draw with.
interface VariableNode extends TreeNodeData {
  variable: Variable;
  children?: VariableNode[];
}
